import { useState, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { Spinner } from "@/shared/components/spinner";
import { cn } from "@/shared/lib/utils";
import type { ParsingIssue } from "@/features/patient/records/api/dataPipelineApi";
import type { ReviewItem } from "@/portals/patient/api/reviewItemsApi";
import { reviewListQuery, uploadIdentityQuery } from "@/portals/patient/api/patientQueries";
import { UploadIdentityCard, shouldShowIdentityCard } from "@/features/patient/records/components/UploadIdentityCard";
import { ERROR_TYPE_LABELS, RESOURCE_TYPE_LABELS, groupIssues, prepareIssues } from "@/features/patient/records/utils/uploadIssuePresentation";
import { QUEUE_PATHS, bulkReviewPath, isFromUpload, isIdentityItem, isOpenItem } from "@/features/patient/human-review/reviewQueues";

/**
 * An upload's Issues, grouped by who can act on them (see the backend's
 * upload_issues.py):
 *   1. Needs your decision — its review items: the identity check (Patient
 *      Only Queue) and medicines / records that disagree (Human Review Queue).
 *      Two bulk buttons open each queue's bulk-review screen for this upload;
 *      items already reviewed stay listed, marked so, with their button off.
 *   2. Couldn't be read — files the patient can fix by uploading again.
 *   3. Couldn't be imported — nothing for the patient to do.
 *   4. Imported with notes — information about the source data only.
 * Each section is a card, closed by default; its arrow opens the issues in it.
 */

const RESOLVED = new Set(["RESOLVED_PATIENT_REPORTED", "RESOLVED_CLINICIAN_VERIFIED"]);

/** Health review items this upload created, open and already reviewed (identity has its own card). */
export function reviewItemsForUpload(items: ReviewItem[], uploadId: string): ReviewItem[] {
  return items.filter((item) => !isIdentityItem(item) && isFromUpload(item, uploadId));
}

/** What a reviewed row says: the patient's part is done; a clinician may still be deciding. */
function reviewedText(item: ReviewItem): string {
  return RESOLVED.has(item.itemStatus) ? "Reviewed" : "With your clinician";
}

function reviewItemTitle(item: ReviewItem): string {
  if (item.triggerType === "GOLDEN_RECORD_CONFLICT") {
    return `Records disagree${item.empi?.conflict ? ` · ${item.empi.conflict.resourceType}` : ""}`;
  }
  const subject = item.records.find((r) => r.recordRole === "SUBJECT" || r.recordRole === "INCOMING") ?? item.records[0];
  const name = subject?.fields?.medication_name;
  return `Check a medicine${typeof name === "string" && name ? ` · ${name}` : ""}`;
}

function Section({ id, title, summary, count, tone, action, children }: {
  id: string;
  title: string;
  summary: string;
  count: number;
  tone: "action" | "warn" | "neutral";
  /** One button for the whole section, under its title (shown open or closed). */
  action?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const bodyId = `upload-issues-${id}`;
  return (
    <section data-testid={`issue-section-${id}`} className="rounded-xl border border-[#E5E7EB] bg-white">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span
          className={cn(
            "flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full px-2 text-xs font-bold",
            tone === "action" ? "bg-[#FFE4EA] text-[#E4002B]" : tone === "warn" ? "bg-[#FEF3C7] text-[#B45309]" : "bg-[#F3F4F6] text-[#6B7280]",
          )}
        >
          {count}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-[#111827]">{title}</span>
          <span className="block text-xs text-[#6B7280]">{summary}</span>
        </span>
        <Icon
          icon="lucide:chevron-down"
          aria-hidden
          className={cn("h-4 w-4 shrink-0 text-[#6B7280] transition-transform", open && "rotate-180")}
        />
      </button>
      {action && <div className="px-4 pb-3 pl-14">{action}</div>}
      {open && (
        <div id={bodyId} className="space-y-2 border-t border-[#F3F4F6] px-4 py-3">
          {children}
        </div>
      )}
    </section>
  );
}

/** One of the two bulk-review buttons at the top of "Needs your decision". */
function BulkButton({ title, description, onClick }: { title: string; description: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl border border-[#F8C9D2] bg-[#FFF5F7] px-4 py-3 text-left transition-colors hover:bg-[#FFE4EA]"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#E4002B]/50">
        <Icon icon="lucide:list-checks" className="h-4 w-4 !text-white" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-[#0A2947]">{title}</span>
        <span className="block text-xs text-[#6B7280]">{description}</span>
      </span>
    </button>
  );
}

/** The one "Upload again" button of the "Couldn't be read" section. */
function UploadAgainButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      className="!flex !h-auto !items-center !gap-1.5 !rounded-lg !bg-[#0A2947] !px-3 !py-1.5 !text-xs !font-semibold !text-white hover:!bg-[#0A2947] hover:!opacity-90"
    >
      <Icon icon="lucide:upload" className="h-3.5 w-3.5 !text-white" />
      Upload again
    </Button>
  );
}

function IssueCard({ issue }: { issue: ParsingIssue & { ordinal?: string } }) {
  return (
    <div className="rounded-xl bg-[#F3F4F6] px-4 py-3" data-testid="issue-card">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="text-sm font-semibold text-[#111827]">
          {RESOURCE_TYPE_LABELS[issue.resource_type] || issue.resource_type}
          {issue.ordinal && ` (${issue.ordinal})`}
        </span>
        {issue.source_file && <span className="text-xs text-[#9CA3AF]">· {issue.source_file}</span>}
      </div>
      <p className="mt-1 text-xs font-semibold text-[#6B7280]">{issue.title || ERROR_TYPE_LABELS[issue.error_type] || issue.error_type}</p>
      <p className="mt-0.5 text-xs text-[#9CA3AF]">{issue.reason}</p>
    </div>
  );
}

export function UploadIssueSections({ uploadId, status, issues, isLoadingContents, actingAsProfileId }: {
  uploadId?: string;
  status: string;
  issues: ParsingIssue[];
  isLoadingContents: boolean;
  actingAsProfileId?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const complete = Boolean(uploadId) && status === "complete";
  const identity = useQuery({ ...uploadIdentityQuery(uploadId ?? "", actingAsProfileId), enabled: complete });
  const reviewList = useQuery({ ...reviewListQuery(actingAsProfileId), enabled: complete });

  const reviewItems = complete && uploadId && reviewList.data ? reviewItemsForUpload(reviewList.data, uploadId) : [];
  const openItems = reviewItems.filter(isOpenItem);
  const showIdentity = shouldShowIdentityCard(identity.data);
  const identityPending = identity.data?.state === "needs_your_confirmation";
  // The badge counts what still needs the patient; reviewed items stay listed.
  const decisionCount = openItems.length + (identityPending ? 1 : 0);
  const hasDecisionItems = reviewItems.length > 0 || showIdentity;
  // Bulk review for this upload; Back on that screen returns here.
  const openBulk = (queue: "health" | "identity") =>
    navigate(bulkReviewPath(queue, uploadId), { state: { returnTo: `${location.pathname}${location.search}` } });
  const groups = groupIssues(issues);
  const openReviewItem = (itemId: string) => {
    const params = new URLSearchParams({ item: itemId });
    if (actingAsProfileId) params.set("profile", actingAsProfileId);
    navigate(`${QUEUE_PATHS.health.page}?${params.toString()}`);
  };
  const uploadAgain = () => navigate("/patient/upload");

  if (!hasDecisionItems && issues.length === 0) {
    return isLoadingContents ? (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-[#8A8A8A]">
        <Spinner />
        Checking for issues…
      </div>
    ) : (
      <EmptyState
        icon={<Icon icon="lucide:check-circle-2" className="h-8 w-8 text-[#16A34A]" />}
        title="Everything looks good"
        description="No issues were found in this upload. Every record was imported successfully."
      />
    );
  }

  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  return (
    <div className="space-y-3" data-testid="upload-issue-sections">
      {hasDecisionItems && (
        <Section
          id="decision"
          title="Needs your decision"
          summary={decisionCount > 0
            ? `${plural(decisionCount, "item", "items")} from this upload ${decisionCount === 1 ? "needs" : "need"} your answer.`
            : "You've answered everything from this upload."}
          count={decisionCount}
          tone="action"
        >
          {(identityPending || openItems.length > 0) && (
            <div className="grid gap-2 sm:grid-cols-2" data-testid="decision-bulk-buttons">
              {identityPending && (
                <BulkButton
                  title="Review identity check"
                  description="Confirm this upload is yours."
                  onClick={() => openBulk("identity")}
                />
              )}
              {openItems.length > 0 && (
                <BulkButton
                  title={`Review all health items (${openItems.length})`}
                  description="Medicines and records from this upload that need your answer."
                  onClick={() => openBulk("health")}
                />
              )}
            </div>
          )}
          {showIdentity && <UploadIdentityCard identity={identity.data!} actingAsProfileId={actingAsProfileId} />}
          {reviewItems.map((item) => {
            const open = isOpenItem(item);
            return (
              <div key={item.id} data-testid="decision-card" className="flex items-center justify-between gap-3 rounded-xl bg-[#F3F4F6] px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#111827]">{reviewItemTitle(item)}</p>
                  <p className={cn("mt-0.5 flex items-center gap-1 text-xs", open ? "text-[#6B7280]" : "font-semibold text-[#16A34A]")}>
                    {!open && <Icon icon="lucide:check-circle-2" className="h-3.5 w-3.5" />}
                    {open ? "Waiting for you" : reviewedText(item)}
                  </p>
                </div>
                <Button
                  onClick={() => openReviewItem(item.id)}
                  disabled={!open}
                  className="!flex !h-auto !shrink-0 !items-center !gap-1.5 !rounded-lg !bg-[#0A2947] !px-3 !py-1.5 !text-xs !font-semibold !text-white hover:!bg-[#0A2947] hover:!opacity-90 disabled:!bg-[#D1D5DB] disabled:!text-[#6B7280] disabled:!opacity-100"
                >
                  {open ? "Review" : "Reviewed"}
                </Button>
              </div>
            );
          })}
        </Section>
      )}
      {groups.unreadable.length > 0 && (
        <Section
          id="unreadable"
          title="Couldn't be read"
          summary={`${plural(groups.unreadable.length, "file", "files")} couldn't be used. You can fix this by uploading ${groups.unreadable.length === 1 ? "it" : "them"} again.`}
          count={groups.unreadable.length}
          tone="warn"
          action={groups.unreadable.some((issue) => issue.action === "upload_again")
            ? <UploadAgainButton onClick={uploadAgain} />
            : undefined}
        >
          {prepareIssues(groups.unreadable).map((issue, index) => (
            <IssueCard key={issue.record_id ?? `${issue.source_file}-${index}`} issue={issue} />
          ))}
        </Section>
      )}
      {groups.not_imported.length > 0 && (
        <Section
          id="not_imported"
          title="Couldn't be imported"
          summary="Part of this upload couldn't be imported. There's nothing you need to do."
          count={groups.not_imported.length}
          tone="neutral"
        >
          {prepareIssues(groups.not_imported).map((issue, index) => (
            <IssueCard key={issue.record_id ?? `${issue.source_file}-${index}`} issue={issue} />
          ))}
        </Section>
      )}
      {groups.note.length > 0 && (
        <Section
          id="note"
          title="Imported with notes"
          summary={`${plural(groups.note.length, "record was", "records were")} imported with notes about missing or unusual details in the source file.`}
          count={groups.note.length}
          tone="neutral"
        >
          {prepareIssues(groups.note).map((issue, index) => (
            <IssueCard key={issue.record_id ?? `${issue.source_file}-${index}`} issue={issue} />
          ))}
        </Section>
      )}
    </div>
  );
}
